import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { getHierarchy, getUserProfile, saveUserProfile } from '../services/authentikAPI';
import toast, { Toaster } from 'react-hot-toast';

const DESIGNATIONS = [
  'Chief Engineer(Ele)', 'Superintending Engineer(Ele)', 'Executive Engineer',
  'Assistant Executive Engineer', 'Assistant Engineer', 'Junior Engineer',
  'Lineman', 'Junior Lineman', 'Power Man', 'Meter Reader', 'AO', 'AAO',
  'Junior Assistant', 'Senior Assistant', 'Office Account'
];

const inp = {
  width: '100%', padding: '10px 14px',
  border: '1.5px solid #D3D1C7', borderRadius: '8px',
  fontSize: '14px', boxSizing: 'border-box',
  color: '#2c2c2a', background: '#fff', outline: 'none'
};

// A field the account already arrived with a value for (e.g. Designation/
// Office/Zone/Circle/Division set by an admin's bulk upload — see
// bulkUsers.js) is shown greyed out and locked, not just pre-selected —
// it was deliberately assigned, not something the person filling this
// form should be able to casually change.
const inpPrefilled = {
  ...inp,
  background: '#E9E9E9', color: '#666', cursor: 'not-allowed',
};

const lbl = {
  display: 'block', fontSize: '13px',
  fontWeight: '500', color: 'rgba(255, 255, 255, 0.85)', marginBottom: '5px'
};

// Groups that bypass profile completion entirely
const ADMIN_GROUPS = ['bescom-super-admins', 'authentik Admins'];

// Same background image + darker/transparent glass card language as
// Login.jsx/Register.jsx — see Login.jsx's own card comment. AddPhone.jsx/
// AddEmail.jsx still use the older lighter card, unchanged.
const page = {
  minHeight: '100vh',
  boxSizing: 'border-box',
  backgroundImage: 'url(/BESCOM_SSO_BG_Image.png)',
  backgroundSize: 'cover',
  backgroundPosition: 'center',
  padding: '40px 20px',
};

const card = {
  maxWidth: '640px', margin: '0 auto',
  background: 'rgba(10, 20, 40, 0.38)',
  backdropFilter: 'blur(14px)',
  WebkitBackdropFilter: 'blur(14px)',
  border: '1px solid rgba(255, 255, 255, 0.22)',
  borderRadius: '16px',
  padding: '48px',
  boxShadow: '0 20px 60px rgba(0, 0, 0, 0.45)',
};

const textMuted = 'rgba(255, 255, 255, 0.75)';
const officeHintColor = '#7FE0C3';

// Centered in-page modal (not a browser confirm()) — same overlay/card
// pattern as IdleTimeoutWatcher.jsx's "Still there?" dialog.
const cautionOverlay = {
  position: 'fixed', inset: 0, background: 'rgba(20, 24, 33, 0.55)',
  display: 'flex', alignItems: 'center', justifyContent: 'center',
  padding: '20px', zIndex: 2000,
};
const cautionCard = {
  background: '#fff', borderRadius: '14px', padding: '40px',
  width: '100%', maxWidth: '560px',
  boxShadow: '0 20px 60px rgba(0, 0, 0, 0.35)',
  textAlign: 'center',
};

export default function CompleteProfile() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(false);
  const [hierarchy, setHierarchy] = useState([]);
  const [userPk, setUserPk] = useState(null);
  const [showCaution, setShowCaution] = useState(true);

  const [form, setForm] = useState({
    designation: '',
    zone: '',
    circle: '',
    division: '',
    subdivision: '',
    section: ''
  });
  // Which fields arrived already set (see inpPrefilled above) — captured
  // once at load time, not recomputed from `form` on every change, so a
  // field stays locked even though its value is technically still present
  // in `form` (cascading resets on a LOWER field don't touch a prefilled
  // higher one anyway, but this keeps the "was this admin-assigned"
  // signal independent of the live form state either way).
  const [prefilled, setPrefilled] = useState({});

  useEffect(() => {
    if (!user) return;

    // If user is in an admin group, skip profile completion entirely
    const userGroups = user?.profile?.groups || [];
    const isAdmin = ADMIN_GROUPS.some(g => userGroups.includes(g));
    if (isAdmin) {
      navigate('/dashboard', { replace: true });
      return;
    }

    // pk comes straight from the token's own claims (OIDC `sub` / our
    // custom JWT's `pk`, both surfaced as profile.sub) — no need to look
    // the user up by mobile number, which is also no longer collected here.
    const pk = user?.profile?.sub || null;
    setUserPk(pk);

    const init = async () => {
      const h = await getHierarchy();
      setHierarchy(h);

      // Pre-fill from whatever's already on the account — mainly for
      // bulk-created accounts (see bulkUsers.js), which arrive with
      // Designation already set by the admin from the onboarding CSV, so
      // the person doesn't have to re-select it. A no-op for anyone
      // without existing attributes (self-registered accounts start
      // blank, same as before).
      if (pk) {
        try {
          const { attributes } = await getUserProfile(pk);
          if (attributes) {
            setForm((prev) => ({
              designation: attributes.designation || prev.designation,
              zone: attributes.zone || prev.zone,
              circle: attributes.circle || prev.circle,
              division: attributes.division || prev.division,
              subdivision: attributes.subdivision || prev.subdivision,
              section: attributes.section || prev.section,
            }));
            setPrefilled({
              designation: !!attributes.designation,
              zone: !!attributes.zone,
              circle: !!attributes.circle,
              division: !!attributes.division,
              subdivision: !!attributes.subdivision,
              section: !!attributes.section,
            });
          }
        } catch (err) {
          // Best-effort — the form just starts blank, same as before this existed.
        }
      }
    };
    init();
  }, [user, navigate]);

  const getZones = () => [...new Set(hierarchy.map(h => h.zone))].filter(Boolean);
  const getCircles = () => !form.zone ? [] : [...new Set(hierarchy.filter(h => h.zone === form.zone).map(h => h.circle))].filter(Boolean);
  const getDivisions = () => !form.circle ? [] : [...new Set(hierarchy.filter(h => h.zone === form.zone && h.circle === form.circle).map(h => h.division))].filter(Boolean);
  const getSubDivisions = () => !form.division ? [] : [...new Set(hierarchy.filter(h => h.zone === form.zone && h.circle === form.circle && h.division === form.division).map(h => h.subdivision))].filter(Boolean);
  const getSections = () => !form.subdivision ? [] : [...new Set(hierarchy.filter(h => h.zone === form.zone && h.circle === form.circle && h.division === form.division && h.subdivision === form.subdivision).map(h => h.section))].filter(Boolean);

  const isZoneOffice = form.zone && hierarchy.some(h => h.zone === form.zone && !h.circle);
  const isCircleOffice = form.circle && hierarchy.some(h => h.zone === form.zone && h.circle === form.circle && !h.division);
  const isDivisionOffice = form.division && hierarchy.some(h => h.zone === form.zone && h.circle === form.circle && h.division === form.division && !h.subdivision && !h.section);
  const isSubdivisionOffice = form.subdivision && hierarchy.some(h => h.zone === form.zone && h.circle === form.circle && h.division === form.division && h.subdivision === form.subdivision && !h.section);
  // RELAXED 2026-10-15 (user's explicit choice): used to require reaching
  // Section or a hierarchy row explicitly flagged as a terminal "office"
  // entry (isZoneOffice etc. above — still used for the disabled/hint
  // logic below, just no longer gates submission) before allowing
  // submission — meant someone genuinely working at, say, Division level
  // had no way to say so unless that exact division also happened to be
  // separately flagged as an office in the uploaded data. The cascading
  // selects (handleChange resets everything below a changed level) already
  // make "gaps" structurally impossible, so once Zone is picked, wherever
  // the person actually stopped is their deliberate answer — matches
  // bescom-contact-directory's own admin.html, which has always allowed
  // this via its own "None (stop at X)" dropdown placeholder, and the
  // admin-side edit of this same data (AdminUserDetail.jsx) which never
  // had this restriction either.
  const canSubmitPosting = !!form.zone;

  const handleChange = useCallback((e) => {
    const { name, value } = e.target;
    setForm(prev => {
      const updated = { ...prev, [name]: value };
      if (name === 'zone') { updated.circle = ''; updated.division = ''; updated.subdivision = ''; updated.section = ''; }
      else if (name === 'circle') { updated.division = ''; updated.subdivision = ''; updated.section = ''; }
      else if (name === 'division') { updated.subdivision = ''; updated.section = ''; }
      else if (name === 'subdivision') { updated.section = ''; }
      return updated;
    });
  }, []);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!form.designation || !canSubmitPosting) {
      toast.error('Please fill Designation and Zone');
      return;
    }
    if (!userPk) {
      toast.error('User not found. Please contact admin.');
      return;
    }
    setLoading(true);
    try {
      await saveUserProfile(userPk, form);
      // Registration sets this whenever the profile was incomplete at
      // that point (see Register.jsx) — it's now stale. Left uncleared,
      // the LATER lazy session-sync redirect (Dashboard.jsx) would land
      // on SsoSyncCallback.jsx, see this still set, and wrongly bounce
      // back here a second time even though the profile is done.
      localStorage.removeItem('bescom_needs_profile');
      toast.success('Profile completed successfully!');
      setTimeout(() => navigate('/dashboard'), 1500);
    } catch (err) {
      toast.error('Failed to save profile');
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div style={page}>
      <Toaster />
      {showCaution && (
        <div style={cautionOverlay}>
          <div style={cautionCard}>
            <div style={{ fontSize: '40px', margin: '0 0 14px' }}>⚠️</div>
            <p style={{ fontSize: '17px', color: '#5F5E5A', margin: '0 0 26px', lineHeight: 1.6 }}>
              Please select your <strong>office hierarchy</strong> carefully to ensure your applications and access permissions are configured correctly.
            </p>
            <button
              type="button"
              onClick={() => setShowCaution(false)}
              style={{
                padding: '12px 36px', background: 'linear-gradient(135deg, #009FE3 0%, #0072B1 100%)',
                color: '#fff', border: 'none', borderRadius: '8px', fontSize: '16px', fontWeight: 600,
                cursor: 'pointer',
              }}
            >
              OK
            </button>
          </div>
        </div>
      )}
      <div style={card}>
        <div style={{ marginBottom: '32px' }}>
          <img src="/bescom-logo-ui.png" alt="BESCOM" style={{ width: '60px', display: 'block', margin: '0 auto 16px' }} />
          <h1 style={{ fontSize: '22px', fontWeight: '700', color: '#D4A94C', margin: '0 0 8px' }}>
            Complete Your Profile
          </h1>
          <p style={{ fontSize: '14px', color: textMuted, margin: 0 }}>
            This information will be used across all BESCOM applications
          </p>
        </div>

        <form onSubmit={handleSubmit}>
          <div style={{ marginBottom: '16px' }}>
            <label style={lbl}>Designation *</label>
            <select
              name="designation" value={form.designation} onChange={handleChange} required
              disabled={prefilled.designation}
              style={prefilled.designation ? inpPrefilled : { ...inp, cursor: 'pointer' }}
            >
              <option value="">-- Select --</option>
              {DESIGNATIONS.map(d => <option key={d}>{d}</option>)}
            </select>
          </div>

          <div style={{ borderTop: '2px solid rgba(255, 255, 255, 0.25)', paddingTop: '24px', marginBottom: '16px' }}>
            <h3 style={{ fontSize: '16px', fontWeight: '600', color: '#009FE3', margin: '0 0 16px' }}>
              Current Posting
            </h3>
          </div>

          <div style={{ marginBottom: '16px' }}>
            <label style={lbl}>Zone/Office *</label>
            <select
              name="zone" value={form.zone} onChange={handleChange} required
              disabled={prefilled.zone}
              style={prefilled.zone ? inpPrefilled : { ...inp, cursor: 'pointer' }}
            >
              <option value="">-- Select --</option>
              {getZones().map(z => <option key={z}>{z}</option>)}
            </select>
            {isZoneOffice && (
              <p style={{ margin: '6px 0 0', fontSize: '12px', color: officeHintColor }}>
                ✅ This is an office location — no further selection needed.
              </p>
            )}
          </div>

          {/* All levels always shown — disabled until their own parent is
              picked, rather than hidden/revealed one at a time, so it's
              clear from the start how many hierarchy levels actually
              exist instead of looking like Zone is the only choice.
              Stopping anywhere from Circle down is always valid now
              (canSubmitPosting above just requires Zone) — picking the
              level's own "— None (stop at X) —" placeholder, same as
              reaching a data-flagged "office" entry (see AdminHierarchy.jsx's
              independent-entry toggle), is a deliberate stopping point,
              not an incomplete one. */}
          <div style={{ marginBottom: '16px' }}>
            <label style={lbl}>Circle</label>
            <select
              name="circle" value={form.circle} onChange={handleChange}
              disabled={prefilled.circle || !form.zone || isZoneOffice}
              style={prefilled.circle ? inpPrefilled : { ...inp, cursor: (form.zone && !isZoneOffice) ? 'pointer' : 'not-allowed', opacity: (form.zone && !isZoneOffice) ? 1 : 0.6 }}
            >
              <option value="">— None (stop at Zone) —</option>
              {getCircles().map(c => <option key={c}>{c}</option>)}
            </select>
            {isCircleOffice && (
              <p style={{ margin: '6px 0 0', fontSize: '12px', color: officeHintColor }}>
                ✅ This is an office location — no further selection needed.
              </p>
            )}
          </div>

          <div style={{ marginBottom: '16px' }}>
            <label style={lbl}>Division</label>
            <select
              name="division" value={form.division} onChange={handleChange}
              disabled={prefilled.division || !form.circle || isCircleOffice}
              style={prefilled.division ? inpPrefilled : { ...inp, cursor: (form.circle && !isCircleOffice) ? 'pointer' : 'not-allowed', opacity: (form.circle && !isCircleOffice) ? 1 : 0.6 }}
            >
              <option value="">— None (stop at Circle) —</option>
              {getDivisions().map(d => <option key={d}>{d}</option>)}
            </select>
            {isDivisionOffice && (
              <p style={{ margin: '6px 0 0', fontSize: '12px', color: officeHintColor }}>
                ✅ This is an office location — no further selection needed.
              </p>
            )}
          </div>

          <div style={{ marginBottom: '16px' }}>
            <label style={lbl}>Sub-Division</label>
            <select
              name="subdivision" value={form.subdivision} onChange={handleChange}
              disabled={prefilled.subdivision || !form.division || isDivisionOffice}
              style={prefilled.subdivision ? inpPrefilled : { ...inp, cursor: (form.division && !isDivisionOffice) ? 'pointer' : 'not-allowed', opacity: (form.division && !isDivisionOffice) ? 1 : 0.6 }}
            >
              <option value="">— None (stop at Division) —</option>
              {getSubDivisions().map(s => <option key={s}>{s}</option>)}
            </select>
            {isSubdivisionOffice && (
              <p style={{ margin: '6px 0 0', fontSize: '12px', color: officeHintColor }}>
                ✅ This is an office location — no further selection needed.
              </p>
            )}
          </div>

          <div style={{ marginBottom: '32px' }}>
            <label style={lbl}>Section</label>
            <select
              name="section" value={form.section} onChange={handleChange}
              disabled={prefilled.section || !form.subdivision || isSubdivisionOffice}
              style={prefilled.section ? inpPrefilled : { ...inp, cursor: (form.subdivision && !isSubdivisionOffice) ? 'pointer' : 'not-allowed', opacity: (form.subdivision && !isSubdivisionOffice) ? 1 : 0.6 }}
            >
              <option value="">— None (stop at Sub-Division) —</option>
              {getSections().map(s => <option key={s}>{s}</option>)}
            </select>
          </div>

          <button type="submit" disabled={loading} style={{
            width: '100%', padding: '14px',
            background: 'linear-gradient(135deg, #009FE3 0%, #0072B1 100%)',
            color: '#fff', border: 'none', borderRadius: '10px',
            fontSize: '16px', fontWeight: '600',
            cursor: loading ? 'not-allowed' : 'pointer',
            opacity: loading ? 0.7 : 1
          }}>
            {loading ? 'Saving...' : 'Complete Profile →'}
          </button>
        </form>
      </div>
    </div>
  );
}
